import { CalendarClock } from "lucide-react"

import type { AbsenceRequest } from "@/lib/absence-tracking-api"

import { Button } from "@/components/button"
import { AbsenceStateBadge } from "@/components/shared/absence-state-badge"
import {
  Table,
  TableBody,
  TableCaption,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table"
import {
  formatGuildDateRange,
  formatGuildDateTime,
} from "@/lib/guild-date-format"

export function AbsenceTable({
  items,
  emptyLabel,
  actionLabel,
  disabledRequestId,
  onAction,
}: {
  items: AbsenceRequest[]
  emptyLabel: string
  actionLabel?: string
  disabledRequestId?: string
  onAction?: (requestId: string) => void
}) {
  if (items.length === 0) {
    return <p className="text-sm text-muted-foreground">{emptyLabel}</p>
  }

  return (
    <Table className="data-table text-left text-sm">
      <TableCaption className="sr-only">Absence list</TableCaption>
      <TableHeader>
        <TableRow>
          <TableHead scope="col">Range</TableHead>
          <TableHead scope="col">Status</TableHead>
          <TableHead scope="col">Reason</TableHead>
          <TableHead scope="col">Updated</TableHead>
          <TableHead scope="col">Action</TableHead>
        </TableRow>
      </TableHeader>
      <TableBody>
        {items.map((item) => {
          const isActionable =
            onAction && (item.state === "pending" || item.state === "approved")

          return (
            <TableRow key={item.id}>
              <TableHead scope="row" className="font-medium">
                <div className="flex items-center gap-2">
                  <CalendarClock className="size-4 text-muted-foreground" />
                  {formatGuildDateRange(item.startAt, item.endAt)}
                </div>
              </TableHead>
              <TableCell>
                <AbsenceStateBadge state={item.state} />
              </TableCell>
              <TableCell className="max-w-xs">
                <span className="line-clamp-2">{item.reason ?? "-"}</span>
              </TableCell>
              <TableCell className="text-muted-foreground">
                {formatGuildDateTime(item.updatedAt)}
              </TableCell>
              <TableCell>
                {isActionable ? (
                  <Button
                    type="button"
                    size="sm"
                    variant="outline"
                    disabled={disabledRequestId === item.id}
                    onClick={() => onAction(item.id)}
                  >
                    {disabledRequestId === item.id ? actionLabel : "Cancel"}
                  </Button>
                ) : (
                  <span className="text-muted-foreground">-</span>
                )}
              </TableCell>
            </TableRow>
          )
        })}
      </TableBody>
    </Table>
  )
}
